import { useEffect, useState, type FormEvent } from 'react'
import { usePageData } from '@/hooks/usePageData'
import { FORMS, submitAuthForm, AuthSubmissionError } from '@/lib/authForm'
import { navigateTo } from '@/lib/navigate'
import FlipButton from '@/components/ui/FlipButton'

/*
  MIG Forgot Password page. Submits modules/portal/account/forgot_password
  which is a `properties.email` form, so the field name on the wire is
  `form[properties_attributes][email]`. The platform sends the reset
  email asynchronously and redirects back here with a flash notice that
  we render as the success state.

  Styling lives in src/styles/pages/auth.css.
*/

interface ForgotPasswordPayload {
  flash_notice: string | null
  flash_alert: string | null
}

export default function ForgotPassword() {
  const payload = usePageData<ForgotPasswordPayload>() ?? { flash_notice: null, flash_alert: null }
  const sent = !!payload.flash_notice

  const [visible, setVisible] = useState(false)
  const [email, setEmail] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(payload.flash_alert ?? null)
  const [done, setDone] = useState(sent)

  useEffect(() => {
    const t = setTimeout(() => setVisible(true), 100)
    return () => clearTimeout(t)
  }, [])

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    if (!email.trim()) {
      setError('Please enter your email.')
      return
    }
    if (!FORMS.forgotPassword) {
      setError('Reset form is not available. Please refresh and try again.')
      return
    }
    setSubmitting(true)
    setError(null)
    try {
      await submitAuthForm('forgotPassword', {
        'form[properties_attributes][email]': email.trim(),
      })
      setDone(true)
    } catch (err) {
      const msg =
        err instanceof AuthSubmissionError ? err.message : 'Could not send the reset email. Please try again.'
      setError(msg)
      setSubmitting(false)
    }
  }

  return (
    <section className="mig-page-section">
      <div className="mig-page-title-row" data-visible={visible}>
        <h1 className="mig-forgot-page-title">Forgot password</h1>
      </div>

      {done ? (
        <div className="mig-auth-state-body">
          <p className="mig-auth-body">
            If an account matches that email, we have sent password reset instructions to your inbox.
            The link expires shortly, so reset your password as soon as you can.
          </p>
          <FlipButton
            onClick={navigateTo('/sign-in')}
            className="mig-auth-cta-link"
          >
            Back to Sign in
          </FlipButton>
        </div>
      ) : (
        <form onSubmit={handleSubmit} noValidate className="mig-form mig-auth-form">
          <p className="mig-auth-body">
            Enter the email associated with your account and we will send you a link to reset your
            password.
          </p>

          <div className="mig-auth-field-row">
            <div className="mig-auth-field-inner">
              <label className="mig-auth-field-label">EMAIL ADDRESS</label>
              <input
                type="email"
                name="email"
                autoComplete="email"
                placeholder="Enter email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={submitting}
                className="mig-auth-input"
              />
            </div>
            <div className="mig-divider-line" />
          </div>

          <div className="mig-auth-action-row">
            {error && <p role="alert" className="mig-auth-error">{error}</p>}
            <FlipButton
              type="submit"
              disabled={submitting}
              className="mig-auth-submit"
              data-pending={submitting}
            >
              {submitting ? 'Sending…' : 'Send reset link'}
            </FlipButton>
          </div>

          <div className="mig-page-back-row">
            <a href="/sign-in" className="mig-auth-back">BACK</a>
            <div className="mig-divider-line" />
          </div>
        </form>
      )}
    </section>
  )
}
